import { EMPTY_TIMELINE_DEFAULT_DURATION } from '@/components/Packaging/constants';
import { selectPackagingTimelineFrameDuration, selectPlayerCurrentFrame } from '@/redux/selectors/packagingSelectors';
import type { OverlayItem } from '@/redux/slices/packagingSlice.types';
import type { RootState } from '@/redux/store';
import { createSelector } from '@reduxjs/toolkit';

export const selectTimelineSlice = (state: RootState) => state?.packagingTimeline;
export const selectHasPastHistoryEntries = createSelector(selectTimelineSlice, (timeline) => timeline.past.length > 0);
export const selectHasFutureHistoryEntries = createSelector(selectTimelineSlice, (timeline) => timeline.future.length > 0);

export const selectTimelineSegments = createSelector(selectTimelineSlice, (timeline) => timeline.present.segments);
export const selectTimelineSegmentsCount = createSelector(selectTimelineSegments, (segments) => (segments || []).length);
export const selectCurrentSegment = createSelector(
  selectTimelineSegments,
  selectPlayerCurrentFrame,
  (segments, currentFrame) => (segments || []).find((segment) => segment.start <= currentFrame && segment.end > currentFrame) ?? null
);

/**
 * Returns total frames number across all segments in the timeline
 */
export const selectTimelineFramesCount = createSelector(selectTimelineSegments, (segments) =>
  (segments || []).reduce((acc, segment) => acc + (segment.end - segment.start), 0)
);

export const selectTimelineDuration = createSelector(
  selectTimelineFramesCount,
  selectPackagingTimelineFrameDuration,
  (framesCount, frameDuration) => framesCount * frameDuration || EMPTY_TIMELINE_DEFAULT_DURATION
);

export const selectTimelineOverlays = createSelector(selectTimelineSlice, (timeline) => timeline.present.overlays);
export const selectOverlayLayerByIndex = createSelector(
  selectTimelineSlice,
  (_: RootState, layerIndex: number) => layerIndex,
  (timeline, layerIndex) => timeline.present.overlays[layerIndex]
);
export const selectOverlayLayersCount = createSelector(selectTimelineOverlays, (layers) => (layers || []).length);
// Returns the layer where overlay with specified draggableId is located
export const selectOverlayLayerByOverlayId = createSelector(
  selectTimelineOverlays,
  (_: RootState, overlayId: OverlayItem['draggableId'] | null) => overlayId,
  (layers, overlayId) => (layers || []).find((layer) => Boolean(layer.find((overlay) => overlay.draggableId === overlayId)))
);

export const selectOutsideTimelineOverlaysOrdered = createSelector(
  selectTimelineOverlays,
  selectTimelineFramesCount,
  (_: RootState, skip: boolean) => skip,
  (_: RootState, skip: boolean, layerIndex: number) => layerIndex,
  (overlays, timelineFramesCount, skip, layerIndex) => {
    // allow skipping calculations is case if list of outside timeline overlays is actually not need in particular case
    if (skip) {
      return null;
    }

    return overlays[layerIndex].filter((overlay) => overlay.end > timelineFramesCount).sort((a, b) => a.start - b.start);
  }
);

export const selectTimelineOverlaysForCurrentFrame = createSelector(selectPlayerCurrentFrame, selectTimelineOverlays, (currentFrame, overlays) => {
  return overlays.flat().flatMap((overlay) => {
    if (currentFrame >= overlay.start && currentFrame < overlay.end) {
      return [overlay];
    } else {
      return [];
    }
  });
});

export const selectPresentTimelineInOrder = createSelector(selectTimelineSegments, selectTimelineOverlays, (segments, overlays) => {
  return [...segments, ...overlays.flat()].sort((a, b) => a.start - b.start);
});

/**
 * Returns the complete timeline state that can be used to fully restore a timeline
 * when applying a template, preserving all file sources, timestamps, positioning, etc.
 */
export const selectCompleteTimelineTemplateFormat = createSelector(selectTimelineSlice, (timeline) => {
  return {
    segments: timeline.present.segments,
    overlays: timeline.present.overlays
  };
});
